/**
 * Custom hook to get authentication token for admin API requests
 *
 * Returns Clerk session token for authenticated requests
 * Handles Clerk session token retrieval with proper error handling
 */

import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useState } from "react";

export type AdminApiFetchInit = RequestInit & {
  headers?: HeadersInit;
};

export function useAdminAuth() {
  const { isLoaded, isSignedIn, getToken, userId } = useAuth();
  const [authError, setAuthError] = useState<string | null>(null);

  // Clear error when auth state changes
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      setAuthError(null);
    }
  }, [isLoaded, isSignedIn]);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    // Wait for Clerk to be loaded
    if (!isLoaded) {
      throw new Error("Clerk no está listo — esperá un momento");
    }

    // Check if user is signed in
    if (!isSignedIn) {
      throw new Error("No autenticado — iniciá sesión para continuar");
    }

    // Try to get token with retries
    let token: string | null = null;
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts && !token) {
      try {
        token = await getToken();
      } catch (err) {
        console.warn(`[useAdminAuth] Error getting token (attempt ${attempts + 1}):`, err);
      }

      if (!token && attempts < maxAttempts - 1) {
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 100 * (attempts + 1)));
      }

      attempts++;
    }

    if (!token) {
      const error = "No se pudo obtener el token de sesión — iniciá sesión nuevamente";
      setAuthError(error);
      throw new Error(error);
    }

    setAuthError(null);

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [getToken, isLoaded, isSignedIn]);

  /** Authenticated fetch for /api/admin/* (Clerk Bearer). */
  const adminFetch = useCallback(
    async (input: RequestInfo | URL, init?: AdminApiFetchInit): Promise<Response> => {
      const authHeaders = await getAuthHeaders();
      const merged = new Headers(init?.headers);
      for (const [k, v] of Object.entries(authHeaders)) {
        merged.set(k, v);
      }
      return fetch(input, {
        ...init,
        headers: merged,
        cache: init?.cache ?? "no-store",
        credentials: init?.credentials ?? "same-origin",
      });
    },
    [getAuthHeaders],
  );

  return { getAuthHeaders, adminFetch, isLoaded, isSignedIn, userId, authError };
}
